"use client"

import { useTranslations } from "next-intl"
import * as React from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Check, Copy, Mail, X, MoreHorizontal, Send, Instagram } from "lucide-react"
import { DockSheet } from "@/components/ui/dock-sheet"

const MESSENGER_LOGO_URL = "https://upload.wikimedia.org/wikipedia/commons/6/63/Facebook_Messenger_logo_2025.svg"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { useMobile } from "@/hooks/use-mobile"
import { useShareActions, ShareContent } from "./share-content"
import type { ListingShareMeta } from "@/lib/story-cards"
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets"

export interface ShareSheetProps {
  url?: string
  title?: string
  image?: string
  price?: number
  currency?: string
  children: React.ReactNode
  side?: React.ComponentProps<typeof PopoverPrimitive.Content>["side"]
  align?: React.ComponentProps<typeof PopoverPrimitive.Content>["align"]
  sideOffset?: number
  alignOffset?: number
  onOpenChange?: (open: boolean) => void
  variant?: "item" | "profile"
  username?: string
  bio?: string
  storyData?: { description?: string; condition?: string; seller?: string }
  listingMeta?: ListingShareMeta
}

function deriveDesktopInitials(primary?: string, secondary?: string): string {
  const pick = (value?: string) => {
    if (!value) return ""
    const trimmed = value.replace(/^@+/, "").trim()
    if (!trimmed) return ""
    const words = trimmed.split(/[\s._-]+/).filter(Boolean)
    if (words.length >= 2) {
      return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`
    }
    return trimmed.slice(0, 2)
  }
  const candidate = pick(primary) || pick(secondary)
  return (candidate || "?").toUpperCase()
}

function DesktopProfileHeader({
  image,
  username,
  title,
}: {
  image?: string
  username?: string
  title?: string
}) {
  const [broken, setBroken] = React.useState(false)
  React.useEffect(() => {
    setBroken(false)
  }, [image])
  const initials = deriveDesktopInitials(username, title)
  const displayName = username ? `@${username.replace(/^@+/, "")}` : title
  const showImage = Boolean(image) && !broken
  return (
    <div className="flex flex-col items-center gap-2 pb-1">
      <div className="w-16 h-16 rounded-full overflow-hidden relative shrink-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-lg select-none">
        {showImage ? (
          <img
            // `w-16 h-16` = 64 CSS px circle -> SQUARE_SM. Guard on `image`,
            // not on the built URL — `imagePresetUrl` resolves relative paths
            // itself, so the outer `resolveStorageUrl` was redundant and only
            // served to widen the argument type to include `undefined`.
            src={image ? imagePresetUrl(image, SQUARE_SM) : undefined}
            alt={displayName || "Profile"}
            className="w-full h-full object-cover"
            onError={() => setBroken(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span aria-hidden>{initials}</span>
        )}
      </div>
      {displayName ? (
        <span className="text-sm font-medium text-foreground dark:text-white truncate max-w-[260px]">
          {displayName}
        </span>
      ) : null}
    </div>
  )
}

function resolveUrl(rawUrl: string | undefined): string {
  if (typeof window === "undefined") return rawUrl ?? ""
  if (!rawUrl) return window.location.href
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl
  if (rawUrl.startsWith("/")) return `${window.location.origin}${rawUrl}`
  return rawUrl
}

export function ShareSheet({
  url,
  title,
  image,
  price,
  currency,
  children,
  side = "right",
  align = "center",
  sideOffset = 12,
  alignOffset = 0,
  onOpenChange,
  variant = "item",
  username,
  bio,
  storyData,
  listingMeta,
}: ShareSheetProps): React.ReactElement {
  const t = useTranslations("shareSheet");
  const { toast } = useToast()
  const isMobile = useMobile()
  const [open, setOpen] = React.useState(false)
  const [showMobileSheet, setShowMobileSheet] = React.useState(false)
  const [showEmailOptions, setShowEmailOptions] = React.useState(false)
  const [showDesktopEmailOptions, setShowDesktopEmailOptions] = React.useState(false)

  const shareActions = useShareActions({
    url,
    title,
    image,
    price,
    currency,
    variant,
    username,
    bio,
    storyData,
    listingMeta,
    onNativeShareSuccess: () => setShowMobileSheet(false),
  })
  const { currentUrl, copied, cardPreviewDataUrl, instagramLoading, handleCopy, handleWhatsApp, handleMessenger, handleTwitter, handleTelegram, handleInstagram, handleTikTok, handleNativeShare } =
    shareActions

  const handleEmailClick = React.useCallback(async () => {
    if (isMobile && typeof navigator !== "undefined" && navigator.share && currentUrl) {
      try {
        await navigator.share({
          title: title || undefined,
          text: title || undefined,
          url: currentUrl,
        })
        setShowMobileSheet(false)
        return
      } catch (error) {
        if ((error as Error)?.name === "AbortError") return
      }
    }

    if (isMobile) {
      setShowEmailOptions(true)
      return
    }

    setShowDesktopEmailOptions((prev) => !prev)
  }, [isMobile, currentUrl, title])

  React.useEffect(() => {
    onOpenChange?.(open || showMobileSheet)
  }, [open, showMobileSheet, onOpenChange])

  React.useEffect(() => {
    if (showMobileSheet && isMobile) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [showMobileSheet, isMobile])

  const closeMobileSheet = React.useCallback(() => {
    setShowMobileSheet(false)
    setShowEmailOptions(false)
  }, [])

  const handleEmailOption = (provider: "gmail" | "outlook" | "yahoo" | "default") => {
    const subject = encodeURIComponent(title || "Check this out!")
    const body = encodeURIComponent(currentUrl)
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`

    if (provider === "default") {
      window.location.href = mailtoUrl
      setShowEmailOptions(false)
      setShowDesktopEmailOptions(false)
      return
    }

    const urls = {
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`,
      outlook: `https://outlook.live.com/mail/0/deeplink/compose?subject=${subject}&body=${body}`,
      yahoo: `https://compose.mail.yahoo.com/?subject=${subject}&body=${body}`,
    }

    const w = window.open(urls[provider], "_blank", "noopener,noreferrer")
    if (!w) {
      toast({
        title: t("popupBlocked"),
        description: t("popupBlockedDesc"),
        variant: "destructive",
      })
    }
    setShowEmailOptions(false)
    setShowDesktopEmailOptions(false)
  }

  const qrCodeUrl = currentUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(currentUrl)}&margin=0`
    : ""

  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isMobile) {
      setShowMobileSheet(true)
    } else {
      setOpen(true)
    }
  }

  const shareContent = (
    <ShareContent
      currentUrl={currentUrl}
      copied={copied}
      title={title}
      image={image}
      price={price}
      currency={currency}
      variant={variant}
      username={username}
      bio={bio}
      cardPreviewDataUrl={cardPreviewDataUrl}
      instagramLoading={instagramLoading}
      onCopy={handleCopy}
      onWhatsApp={handleWhatsApp}
      onMessenger={handleMessenger}
      onTwitter={handleTwitter}
      onTelegram={handleTelegram}
      onInstagram={handleInstagram}
      onTikTok={handleTikTok}
      onEmailClick={handleEmailClick}
      onMore={handleNativeShare}
    />
  )

  const emailOptionsContent = (
    <div className="p-4 space-y-2">
        <button
          onClick={() => handleEmailOption("gmail")}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted hover:bg-accent transition-colors dark:bg-white/5 dark:hover:bg-white/10"
        >
          <div className="w-10 h-10 rounded-full bg-background dark:bg-white flex items-center justify-center">
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path
                fill="#EA4335"
                d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
              />
            </svg>
          </div>
          <span className="text-foreground dark:text-white font-medium">{t("gmail")}</span>
        </button>
        <button
          onClick={() => handleEmailOption("outlook")}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted hover:bg-accent transition-colors dark:bg-white/5 dark:hover:bg-white/10"
        >
          <div className="w-10 h-10 rounded-full bg-background dark:bg-white flex items-center justify-center">
            <svg className="w-6 h-6" viewBox="0 0 103.17322 104.31332">
              <path d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z" fill="#0072c6" />
              <path d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z" fill="#0072c6" />
              <g transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)">
                <path d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z" fill="#0072c6" />
                <path d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z" fill="#0072c6" />
              </g>
            </svg>
          </div>
          <span className="text-foreground dark:text-white font-medium">{t("outlook")}</span>
        </button>
        <button
          onClick={() => handleEmailOption("yahoo")}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted hover:bg-accent transition-colors dark:bg-white/5 dark:hover:bg-white/10"
        >
          <div className="w-10 h-10 rounded-full bg-background dark:bg-white flex items-center justify-center">
            <svg className="w-6 h-6" viewBox="0 0 48 48">
              <polygon fill="#5e35b1" points="4.209,14.881 11.632,14.881 16.189,26.715 20.966,14.881 28.315,14.881 17.07,42 9.501,42 12.587,34.96"/>
              <circle cx="29.276" cy="30.522" r="4.697" fill="#5e35b1"/>
              <polygon fill="#5e35b1" points="34.693,6 27.213,24.042 35.444,24.042 42.925,6"/>
            </svg>
          </div>
          <span className="text-foreground dark:text-white font-medium">{t("yahoo")}</span>
        </button>
        <button
          onClick={() => handleEmailOption("default")}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted hover:bg-accent transition-colors dark:bg-white/5 dark:hover:bg-white/10"
        >
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center dark:bg-white/20">
            <Mail className="w-6 h-6 text-foreground dark:text-white" />
          </div>
          <span className="text-foreground dark:text-white font-medium">{t("defaultEmail")}</span>
        </button>
    </div>
  )

  return (
    <>
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen)
          if (!isOpen) setShowDesktopEmailOptions(false)
        }}
      >
        <PopoverPrimitive.Anchor asChild>
          <div onClick={handleShareClick} onPointerDown={(e) => e.stopPropagation()} className="cursor-pointer">
            {children}
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-[60] w-[340px] rounded-2xl border border-border bg-card p-4 text-foreground shadow-2xl outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-white/10 dark:bg-neutral-900 dark:text-white"
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            avoidCollisions
            collisionPadding={16}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={() => setOpen(false)}
          >
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-foreground dark:text-white">{t("title")}</h3>

              {(variant === "profile" || variant === "item") && cardPreviewDataUrl ? (
                <div className="flex justify-center pb-1">
                  <div className="w-full max-w-[132px] aspect-[9/16] rounded-2xl overflow-hidden border border-border dark:border-white/10 bg-black shrink-0">
                    <img
                      src={cardPreviewDataUrl}
                      alt=""
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              ) : variant === "profile" && !cardPreviewDataUrl && username ? (
                <DesktopProfileHeader image={image} username={username} title={title} />
              ) : null}

              <div className="flex gap-2">
                <Input
                  readOnly
                  value={currentUrl}
                  tabIndex={-1}
                  className="h-10 text-sm text-muted-foreground bg-muted border-border rounded-full px-4 pointer-events-none select-none dark:bg-white/5 dark:border-white/10 dark:text-white/70"
                />
                <Button
                  size="sm"
                  onClick={handleCopy}
                  className="h-10 px-4 shrink-0 min-w-[90px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1.5" />
                      {t("copied")}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" />
                      {t("copy")}
                    </>
                  )}
                </Button>
              </div>

              <div className="flex flex-col items-center py-4 border-t border-b border-border dark:border-white/10">
                <div className="bg-background dark:bg-white p-2 rounded-xl">
                  <img
                    src={qrCodeUrl || "/placeholder.svg"}
                    alt="QR Code"
                    width={120}
                    height={120}
                    className="block"
                    crossOrigin="anonymous"
                  />
                </div>
                <span className="text-xs text-muted-foreground dark:text-white/50 mt-2">{t("scanToOpen")}</span>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={handleWhatsApp}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">WhatsApp</span>
                </button>

                <button
                  onClick={handleMessenger}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00B2FF] to-[#006AFF] flex items-center justify-center p-1.5">
                    <img src={MESSENGER_LOGO_URL} alt="" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">Messenger</span>
                </button>

                <button
                  onClick={handleTwitter}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">X</span>
                </button>

                <button
                  onClick={handleTelegram}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-[#0088cc] flex items-center justify-center">
                    <Send className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">Telegram</span>
                </button>

                <button
                  onClick={handleInstagram}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">Instagram</span>
                </button>

                <button
                  onClick={handleTikTok}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                    </svg>
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">TikTok</span>
                </button>

                <button
                  onClick={handleEmailClick}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-accent transition-colors dark:hover:bg-white/5"
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center dark:bg-white/10">
                    <Mail className="w-5 h-5 text-foreground dark:text-white" />
                  </div>
                  <span className="text-xs text-muted-foreground dark:text-white/70">{t("email")}</span>
                </button>
              </div>

              {showDesktopEmailOptions && (
                <div className="border-t border-border dark:border-white/10 pt-3 space-y-1">
                  <button
                    onClick={() => handleEmailOption("gmail")}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors dark:hover:bg-white/5"
                  >
                    <div className="w-8 h-8 rounded-full bg-background dark:bg-white flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#EA4335"
                          d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
                        />
                      </svg>
                    </div>
                    <span className="text-sm text-foreground dark:text-white/90">{t("gmail")}</span>
                  </button>
                  <button
                    onClick={() => handleEmailOption("outlook")}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors dark:hover:bg-white/5"
                  >
                    <div className="w-8 h-8 rounded-full bg-background dark:bg-white flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 103.17322 104.31332">
                        <path d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z" fill="#0072c6" />
                        <path d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z" fill="#0072c6" />
                        <g transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)">
                          <path d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z" fill="#0072c6" />
                          <path d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z" fill="#0072c6" />
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm text-foreground dark:text-white/90">{t("outlook")}</span>
                  </button>
                  <button
                    onClick={() => handleEmailOption("yahoo")}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors dark:hover:bg-white/5"
                  >
                    <div className="w-8 h-8 rounded-full bg-background dark:bg-white flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 48 48">
                        <polygon fill="#5e35b1" points="4.209,14.881 11.632,14.881 16.189,26.715 20.966,14.881 28.315,14.881 17.07,42 9.501,42 12.587,34.96"/>
                        <circle cx="29.276" cy="30.522" r="4.697" fill="#5e35b1"/>
                        <polygon fill="#5e35b1" points="34.693,6 27.213,24.042 35.444,24.042 42.925,6"/>
                      </svg>
                    </div>
                    <span className="text-sm text-foreground dark:text-white/90">{t("yahoo")}</span>
                  </button>
                  <button
                    onClick={() => handleEmailOption("default")}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors dark:hover:bg-white/5"
                  >
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center dark:bg-white/20">
                      <Mail className="w-5 h-5 text-foreground dark:text-white" />
                    </div>
                    <span className="text-sm text-foreground dark:text-white/90">{t("defaultEmail")}</span>
                  </button>
                </div>
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {isMobile && (
        <DockSheet
          open={showMobileSheet}
          onOpenChange={(next) => {
            if (!next) closeMobileSheet()
          }}
          title={showEmailOptions ? t("chooseEmail") : t("title")}
          onBack={showEmailOptions ? () => setShowEmailOptions(false) : undefined}
        >
          <AnimatePresence mode="wait" initial={false}>
            {showEmailOptions ? (
              <motion.div
                key="email"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {emailOptionsContent}
              </motion.div>
            ) : (
              <motion.div
                key="share"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                {shareContent}
              </motion.div>
            )}
          </AnimatePresence>
        </DockSheet>
      )}
    </>
  )
}
